import { sendLogToServer } from "../../services/logger-service"
import { summarizeErrorMessage } from "../../../core/utils/error-message"

const LOG_SOURCE = "In-App Comms"

export interface DeliveryFetchError extends Error {
    status?: number | null
}

// Builds the error stored in state when a delivery fetch fails with an HTTP
// status (or, when there is none, a failure kind such as NETWORK_ERROR). The
// status is kept in both the message and `status`.
export function deliveryHttpError(status: number | null | undefined, problem?: string): DeliveryFetchError {
    const detail = status ? `HTTP ${status}` : (problem || "no response")
    return Object.assign(new Error(`Failed to fetch in-app delivery messages (${detail})`), { status })
}

// API.makeCall rejects with the apisauce response object (not an Error) for any
// non-ok response, so a failed call arrives here as `{ ok: false, status, problem }`.
export function getHttpStatus(err: unknown): number | undefined {
    if (err instanceof Error || typeof err !== "object" || err === null) { return undefined }
    const status = (err as { status?: unknown }).status
    return typeof status === "number" ? status : undefined
}

// Normalises whatever a failed delivery fetch threw or rejected with.
export function toDeliveryFetchError(err: unknown): DeliveryFetchError {
    if (err instanceof Error) { return err }
    if (typeof err === "object" && err !== null) {
        const response = err as { status?: unknown, problem?: unknown }
        return deliveryHttpError(
            typeof response.status === "number" ? response.status : undefined,
            typeof response.problem === "string" ? response.problem : undefined
        )
    }
    return new Error(String(err))
}

// A short, human-readable reason for a failed call, for showing to the user.
// Accepts an Error, or the apisauce response API.makeCall rejects with (or
// resolves, for a not-ok response), preferring the server's own message.
export function describeRequestFailure(err: unknown): string {
    if (err instanceof Error) {
        return summarizeErrorMessage(err.message) || "Unknown error"
    }
    if (typeof err === "object" && err !== null) {
        const response = err as { status?: unknown, problem?: unknown, data?: { message?: unknown } | null }
        const serverMessage = typeof response.data?.message === "string"
            ? summarizeErrorMessage(response.data.message)
            : ""
        const status = typeof response.status === "number" ? `HTTP ${response.status}` : ""
        if (serverMessage) {
            return status ? `${serverMessage} (${status})` : serverMessage
        }
        if (status) { return status }
        if (typeof response.problem === "string") { return response.problem }
        return "No response from the server"
    }
    return String(err) || "Unknown error"
}

// The log message states only the HTTP status or the error text — never
// communication content, user identifiers or tokens.
export function describeDeliveryFailure(error: DeliveryFetchError): string {
    if (error.status) {
        return `In-app communications delivery fetch failed: HTTP ${error.status}`
    }
    return `In-app communications delivery fetch failed: ${error.message || "unknown error"}`
}

// Posts one WARN entry to the SMRT Link server log. Best effort: a rejected or
// not-ok post is ignored — no retry, no queue, no indicator.
export async function logDeliveryFailure(error: DeliveryFetchError): Promise<void> {
    try {
        await sendLogToServer({
            sourceId: LOG_SOURCE,
            level: "WARN",
            message: describeDeliveryFailure(error),
        })
    } catch {
        // intentionally ignored
    }
}
